import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ResponsiveDialog, ResponsiveDialogBody, ResponsiveDialogContent, ResponsiveDialogDescription, ResponsiveDialogFooter, ResponsiveDialogHeader, ResponsiveDialogTitle } from "@/components/ui/responsive-dialog";
import api from "@/lib/api";

const PHRASE = "SLETT ALT";

/** Admin: wipe all operational data (demo) before real use. Users + settings are kept. */
export const DangerZone = () => {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (confirm.trim().toUpperCase() !== PHRASE) return toast.error(`Skriv «${PHRASE}» for å bekrefte.`);
    setBusy(true);
    try {
      const r = await api.post("/settings/reset-data", { confirm: confirm.trim() });
      setResult(r.data.deleted);
      toast.success("Alle demodata er sletta. Systemet er klart for ekte kundar og maskiner.");
      setConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikkje tømme data.");
    } finally {
      setBusy(false);
    }
  };

  const total = result ? Object.values(result).reduce((a, b) => a + (b || 0), 0) : 0;

  return (
    <Card className="border-[rgba(217,45,32,0.35)] shadow-sm" data-testid="danger-zone-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 font-heading text-base font-bold text-[rgb(var(--status-overdue))]"><AlertTriangle className="h-4 w-4" /> Faresone</CardTitle>
        <CardDescription>Tøm demodata før de tek systemet i bruk. Brukarar, firmaprofil og funksjonsval blir behaldne.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
          <li>Slettar alle kundar, maskiner, timeavlesingar, filter/oljer, rapportar, fristlogg og opplasta filer.</li>
          <li>Rapportnummer startar på nytt (SR-/JR-ÅÅÅÅ-0001).</li>
          <li>Kan ikkje angrast.</li>
        </ul>
        <Button variant="outline" className="border-[rgba(217,45,32,0.4)] text-[rgb(var(--status-overdue))] hover:bg-[rgba(217,45,32,0.06)]" onClick={() => setOpen(true)} data-testid="reset-data-open-button">
          <Trash2 /> Tøm demodata …
        </Button>
        {result && (
          <p className="text-xs text-muted-foreground" data-testid="reset-data-result">Sist tømt no: {total} dokument sletta.</p>
        )}
      </CardContent>

      <ResponsiveDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setConfirm(""); }}>
        <ResponsiveDialogContent className="sm:max-w-md" data-testid="reset-data-dialog">
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-[rgb(var(--status-overdue))]" /> Tøm alle demodata?</ResponsiveDialogTitle>
            <ResponsiveDialogDescription>Dette slettar alt innhald permanent – kundar, maskiner, rapportar og filer. Brukarkontoar og innstillingar blir behaldne.</ResponsiveDialogDescription>
          </ResponsiveDialogHeader>
          <ResponsiveDialogBody className="space-y-2">
            <Label htmlFor="reset-confirm" className="text-sm font-semibold">Skriv <span className="font-mono">{PHRASE}</span> for å bekrefte</Label>
            <Input id="reset-confirm" autoComplete="off" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={PHRASE} className="h-11 font-mono uppercase" data-testid="reset-data-confirm-input" />
          </ResponsiveDialogBody>
          <ResponsiveDialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="reset-data-cancel-button">Avbryt</Button>
            <Button variant="destructive" disabled={busy || confirm.trim().toUpperCase() !== PHRASE} onClick={run} data-testid="reset-data-confirm-button">
              {busy ? <Loader2 className="animate-spin" /> : <Trash2 />} Slett alt
            </Button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </Card>
  );
};
